import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

import { PageBreadcrumbComponent } from '../../../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ConfirmDialogService } from '../../../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../../../shared/services/toast.service';
import { HasPermissionDirective } from '../../../../../shared/directives/has-permission.directive';
import { TicketsConfigApiService } from '../../../services/tickets-config-api.service';
import { TicketsRootCauseDrawerService } from '../../../services/tickets-config-drawer.services';
import { TicketRootCause } from '../../../models/tickets-config.model';

interface RootCauseRow extends TicketRootCause {
  depth: number;
  parentName: string;
}

@Component({
  selector: 'app-tickets-root-causes-list',
  standalone: true,
  imports: [CommonModule, FormsModule, PageBreadcrumbComponent, HasPermissionDirective],
  templateUrl: './root-causes-list.component.html'
})
export class TicketsRootCausesListComponent implements OnInit, OnDestroy {
  items: TicketRootCause[] = [];
  rows: RootCauseRow[] = [];
  filtered: RootCauseRow[] = [];
  loading = false;
  error = '';

  filtroNombre = '';
  filtroSoloActivas = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    protected router: Router,
    private configApi: TicketsConfigApiService,
    private drawer: TicketsRootCauseDrawerService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    this.cargar();
    this.drawer.entitySaved$.pipe(takeUntil(this.destroy$)).subscribe(() => this.cargar());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  cargar(): void {
    this.loading = true;
    this.error = '';
    this.configApi.getRootCauses().pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        this.items = data;
        this.rows = this.buildHierarchy(data);
        this.aplicarFiltros();
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar causas raíz';
        this.loading = false;
      }
    });
  }

  private buildHierarchy(items: TicketRootCause[]): RootCauseRow[] {
    const byParent = new Map<number | null, TicketRootCause[]>();
    for (const it of items) {
      const key = it.idParent ?? null;
      if (!byParent.has(key)) byParent.set(key, []);
      byParent.get(key)!.push(it);
    }
    for (const list of byParent.values()) {
      list.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    }
    const nameById = new Map<number, string>();
    items.forEach((i) => nameById.set(i.id, i.name));

    const out: RootCauseRow[] = [];
    const dfs = (parentId: number | null, depth: number) => {
      const children = byParent.get(parentId) ?? [];
      for (const c of children) {
        out.push({
          ...c,
          depth,
          parentName: c.idParent != null ? nameById.get(c.idParent) ?? '' : ''
        });
        dfs(c.id, depth + 1);
      }
    };
    dfs(null, 0);
    return out;
  }

  aplicarFiltros(): void {
    const term = this.filtroNombre.trim().toLowerCase();
    this.filtered = this.rows.filter((r) => {
      if (term && !r.name.toLowerCase().includes(term)) return false;
      if (this.filtroSoloActivas && !r.isActive) return false;
      return true;
    });
  }

  nuevo(): void {
    this.drawer.open();
  }

  editar(item: TicketRootCause): void {
    this.drawer.open({ id: item.id });
  }

  toggleActiva(item: TicketRootCause): void {
    this.configApi
      .updateRootCause(item.id, {
        name: item.name,
        description: item.description ?? undefined,
        idParent: item.idParent ?? undefined,
        sortOrder: item.sortOrder,
        isActive: !item.isActive
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.cargar(),
        error: (err) => {
          this.toast.error({
            title: 'Error',
            message: err.error?.message || 'No se pudo actualizar la causa raíz'
          });
        }
      });
  }

  async eliminar(item: TicketRootCause): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar causa raíz',
      message: `¿Eliminar la causa raíz "${item.name}"?`
    });
    if (!ok) return;
    this.configApi.deleteRootCause(item.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.toast.deleted('Causa raíz', `Se eliminó "${item.name}" correctamente.`);
        this.cargar();
      },
      error: (err) => {
        this.toast.error({
          title: 'Error',
          message: err.error?.message || 'No se pudo eliminar la causa raíz'
        });
      }
    });
  }

  volverATickets(): void {
    this.router.navigate(['/tickets']);
  }

  indentChars(depth: number): string {
    return '— '.repeat(depth);
  }
}
